import React, { useState } from 'react';
import {
  TextField,
  Typography,
  Stack,
  Container,
  Card,
  Button,
  Box,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSnackbar } from 'notistack';
import usersService from '../api/services/users';

const SignUpSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
  phone: Yup.string()
    .required('Phone number is required')
    .test('is-valid-tel', 'Phone number is invalid', (value) =>
      matchIsValidTel(value ?? '')
    ),
});

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as
  | string
  | undefined;

const SignUp = () => {
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const from = location.state?.from;

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
    },
    validationSchema: SignUpSchema,
    onSubmit: async ({ confirmPassword: _confirm, ...user }) => {
      setLoading(true);
      try {
        const recaptchaToken = RECAPTCHA_SITE_KEY
          ? await recaptchaRef.current?.executeAsync()
          : undefined;
        await usersService.post({
          ...user,
          recaptchaToken: recaptchaToken ?? undefined,
        });
        setSuccess(true);
      } catch (e: any) {
        snackbar.enqueueSnackbar(
          e?.response?.data?.message ||
            'Unable to sign up. You may already have an account.',
          { variant: 'error' }
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const field = (
    name: keyof typeof formik.values,
    label: string,
    type = 'text'
  ) => (
    <TextField
      label={label}
      variant="outlined"
      fullWidth
      type={type}
      sx={{ mb: 2 }}
      {...formik.getFieldProps(name)}
      error={Boolean(formik.touched[name] && formik.errors[name])}
      helperText={formik.touched[name] ? String(formik.errors[name] ?? '') : ''}
    />
  );

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: '#f6f9fb' }}
    >
      <Container maxWidth="sm">
        <Card sx={{ padding: 4 }}>
          {success ? (
            <Stack direction="column" alignItems="center" spacing={2}>
              <Typography variant="h5">Your account is ready</Typography>
              <Typography variant="body1" textAlign="center">
                Log in to send your request to dealers.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() =>
                  navigate('/login', { state: from ? { from } : undefined })
                }
              >
                Log In
              </Button>
            </Stack>
          ) : (
            <Stack direction="column" spacing={2}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  DealerScout
                </Typography>
              </Link>
              <span>
                <Typography variant="h4" sx={{ fontWeight: 700 }} fontSize={32}>
                  Create your free account
                </Typography>
                <Typography variant="body2">
                  Dealers only see your details if you accept their offer.
                </Typography>
              </span>
              <form onSubmit={formik.handleSubmit}>
                {field('firstName', 'First Name')}
                {field('lastName', 'Last Name')}
                <MuiTelInput
                  value={formik.values.phone}
                  onChange={(value) => formik.setFieldValue('phone', value)}
                  onlyCountries={['US']}
                  defaultCountry="US"
                  fullWidth
                  sx={{ mb: 2 }}
                  label="Phone Number"
                  forceCallingCode
                  helperText={
                    formik.touched.phone
                      ? String(formik.errors.phone ?? '')
                      : ''
                  }
                  error={Boolean(formik.touched.phone && formik.errors.phone)}
                />
                {field('email', 'Email', 'email')}
                {field('password', 'Password', 'password')}
                {field('confirmPassword', 'Confirm Password', 'password')}
                {RECAPTCHA_SITE_KEY && (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    size="invisible"
                  />
                )}
                <LoadingButton
                  variant="contained"
                  sx={{ mb: 2 }}
                  fullWidth
                  type="submit"
                  loading={loading}
                >
                  Sign Up
                </LoadingButton>
                <Typography variant="subtitle2" align="center">
                  Already have an account? <Link to="/login">Log in</Link>
                </Typography>
              </form>
            </Stack>
          )}
        </Card>
      </Container>
    </Box>
  );
};

export default SignUp;
